import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarCheck,
  Clock,
  X,
  Bot,
  User,
  Loader2,
  Phone,
  CalendarX,
  Video as VideoIcon,
  Plus,
  Link2,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import ScheduleInterviewDialog from "@/components/ScheduleInterviewDialog";
import InterviewSuccessDialog from "@/components/InterviewSuccessDialog";
import CandidateAdditionalInfoDialog from "@/components/CandidateAdditionalInfoDialog";

const WEEKDAY_LABEL_PT = [
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
  "Domingo",
];

const formatInterview = (iso) => {
  try {
    const d = new Date(iso);
    const wd = WEEKDAY_LABEL_PT[(d.getDay() + 6) % 7]; // Mon=0
    const hh = d.getHours().toString().padStart(2, "0");
    const mm = d.getMinutes().toString().padStart(2, "0");
    return `${wd}, ${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, "0")} às ${hh}:${mm}`;
  } catch {
    return iso;
  }
};

const STATUS_STYLES = {
  confirmed: "bg-emerald-50 border-emerald-200 text-emerald-800",
  cancelled: "bg-slate-100 border-slate-200 text-slate-500",
  rescheduled: "bg-amber-50 border-amber-200 text-amber-800",
};

const JobInterviewsPanel = ({ jobId, job }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [successData, setSuccessData] = useState(null); // {interview, room}
  const [loadingLinksFor, setLoadingLinksFor] = useState(null);
  const [additionalInfoCandidateId, setAdditionalInfoCandidateId] = useState(null);

  const handleShowLinks = async (row) => {
    setLoadingLinksFor(row.id);
    try {
      const res = await axios.post(`${API}/video/interviews/${row.id}/room`, {});
      setSuccessData({ interview: row, room: res.data });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Não foi possível recuperar os links desta entrevista.",
      );
    } finally {
      setLoadingLinksFor(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/jobs/${jobId}/interviews`);
      setRows(res.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) load();
  }, [jobId]);

  const handleCancel = async (id) => {
    if (!window.confirm("Cancelar esta entrevista?")) return;
    try {
      await axios.delete(`${API}/interviews/${id}`);
      toast.success("Entrevista cancelada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cancelar");
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando entrevistas…
      </div>
    );
  }

  const upcoming = rows.filter(
    (r) => r.status === "confirmed" && new Date(r.starts_at) >= new Date()
  );
  const past = rows.filter(
    (r) => r.status !== "confirmed" || new Date(r.starts_at) < new Date()
  );

  return (
    <section
      data-testid="job-interviews-panel"
      className="bg-white border border-slate-200 rounded-lg p-5"
    >
      <div className="flex items-start gap-2 mb-4">
        <div className="w-9 h-9 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
          <CalendarCheck className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h2 className="heading text-base font-semibold text-slate-900 leading-tight">
            Entrevistas agendadas
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Inclui agendamentos feitos pela IA e manualmente pelo RH.
          </p>
        </div>
        <Button
          data-testid="open-schedule-interview"
          onClick={() => setScheduleOpen(true)}
          size="sm"
          className="bg-brand-blue hover:bg-brand-blue-dark text-white h-8 px-3 shrink-0"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Agendar
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
          <CalendarX className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-600 mb-3">
            Nenhuma entrevista agendada ainda para esta vaga.
          </p>
          <Button
            data-testid="schedule-from-empty"
            onClick={() => setScheduleOpen(true)}
            size="sm"
            variant="outline"
            className="border-brand-blue text-brand-blue hover:bg-brand-blue/5"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Agendar primeira entrevista
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {upcoming.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                Próximas ({upcoming.length})
              </p>
              <ul className="space-y-1.5">
                {upcoming.map((r) => (
                  <InterviewRow
                    key={r.id}
                    row={r}
                    onCancel={() => handleCancel(r.id)}
                    onShowLinks={() => handleShowLinks(r)}
                    loadingLinks={loadingLinksFor === r.id}
                  />
                ))}
              </ul>
            </div>
          )}
          {past.length > 0 && (
            <details className="pt-2 border-t border-slate-100">
              <summary className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 cursor-pointer">
                Histórico ({past.length})
              </summary>
              <ul className="space-y-1.5 mt-2">
                {past.map((r) => (
                  <InterviewRow key={r.id} row={r} readOnly />
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <ScheduleInterviewDialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        jobId={jobId}
        defaultManagerId={job?.manager_id || ""}
        onScheduled={(interview, room) => {
          setScheduleOpen(false);
          setSuccessData({ interview, room });
          load();
        }}
      />

      <InterviewSuccessDialog
        open={!!successData}
        onClose={() => setSuccessData(null)}
        interview={successData?.interview}
        room={successData?.room}
        candidateId={successData?.interview?.candidate_id || ""}
        candidateName={successData?.interview?.candidate_name || ""}
        candidatePhone={successData?.interview?.candidate_phone || ""}
        jobTitle={successData?.interview?.job_title || job?.title || ""}
        onCompleteInfo={() => {
          const candidateId = successData?.interview?.candidate_id || "";
          setSuccessData(null);
          setAdditionalInfoCandidateId(candidateId);
        }}
      />

      <CandidateAdditionalInfoDialog
        open={!!additionalInfoCandidateId}
        onClose={() => setAdditionalInfoCandidateId(null)}
        candidateId={additionalInfoCandidateId}
      />
    </section>
  );
};

const InterviewRow = ({ row, onCancel, onShowLinks, loadingLinks, readOnly }) => {
  const navigate = useNavigate();
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    if (!row.candidate_id) return;
    setDownloadingPdf(true);
    try {
      const res = await axios.get(
        `${API}/candidates/${row.candidate_id}/additional-info.pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const safeName =
        (row.candidate_name || "candidato")
          .normalize("NFD")
          .replace(/[̀-ͯ]/g, "")
          .replace(/[^A-Za-z0-9_-]+/g, "_")
          .slice(0, 60) || "candidato";
      const a = document.createElement("a");
      a.href = url;
      a.download = `Dados_Complementares_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Download iniciado");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Não consegui gerar o PDF.",
      );
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <li
      data-testid={`interview-${row.id}`}
      className={`rounded-md border px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${
        STATUS_STYLES[row.status] || STATUS_STYLES.confirmed
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-xs">
          <Clock className="w-3.5 h-3.5" />
          <span className="font-semibold">{formatInterview(row.starts_at)}</span>
          {row.booked_via === "ai" && (
            <span
              className="inline-flex items-center gap-0.5 bg-violet-100 text-violet-700 text-[10px] px-1.5 py-0.5 rounded font-semibold"
              title="Agendado automaticamente pela IA"
            >
              <Bot className="w-2.5 h-2.5" />
              IA
            </span>
          )}
          {row.status !== "confirmed" && (
            <span className="text-[10px] uppercase font-bold tracking-wider">
              {row.status === "cancelled" ? "cancelada" : row.status}
            </span>
          )}
          {row.recording_count > 0 && (
            <span
              className="inline-flex items-center gap-0.5 bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0.5 rounded font-semibold"
              title={`${row.recording_count} gravação(ões) disponível(eis)`}
            >
              <VideoIcon className="w-2.5 h-2.5" />
              {row.recording_count}
            </span>
          )}
          {row.confirmed_by_candidate && (
            <span
              data-testid={`confirmed-${row.id}`}
              className="inline-flex items-center gap-0.5 bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0.5 rounded font-semibold"
              title="Candidato confirmou presença"
            >
              ✓ Confirmado
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] mt-1 text-slate-700">
          <span className="inline-flex items-center gap-1">
            <User className="w-3 h-3" /> {row.candidate_name || "—"}
          </span>
          {row.candidate_phone && (
            <span className="inline-flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {row.candidate_phone}
            </span>
          )}
          <span className="text-slate-500">Gestor: {row.manager_name || "—"}</span>
        </div>
      </div>
      {!readOnly && row.status === "confirmed" && (
        <div className="flex items-center gap-1.5 self-start sm:self-center">
          <Button
            size="sm"
            onClick={() => navigate(`/entrevistas/${row.id}/sala`)}
            className="bg-brand-blue hover:bg-brand-blue-dark text-white h-7 text-[11px] px-2.5"
            data-testid={`start-video-${row.id}`}
            title="Entrar na sala de vídeo"
          >
            <VideoIcon className="w-3 h-3 mr-1" />
            Vídeo
          </Button>
          {onShowLinks && (
            <Button
              size="sm"
              variant="outline"
              onClick={onShowLinks}
              disabled={loadingLinks}
              className="h-7 text-[11px] px-2.5 border-violet-200 text-violet-700 hover:bg-violet-50"
              data-testid={`show-links-${row.id}`}
              title="Ver e compartilhar os links da entrevista"
            >
              {loadingLinks ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <>
                  <Link2 className="w-3 h-3 mr-1" />
                  Links
                </>
              )}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleDownloadPdf}
            disabled={downloadingPdf || !row.candidate_id}
            className="h-7 text-[11px] px-2.5 border-slate-200 text-slate-700 hover:bg-slate-50"
            data-testid={`download-additional-info-${row.id}`}
            title="Baixar PDF com os dados complementares do candidato"
          >
            {downloadingPdf ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <>
                <Download className="w-3 h-3 mr-1" />
                PDF
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onCancel}
            className="text-red-600 hover:bg-red-50"
            data-testid={`cancel-interview-${row.id}`}
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </li>
  );
};

export default JobInterviewsPanel;
